import React, { useState, useEffect, useCallback, useRef } from 'react';
import AuthContext from './AuthContext';
import keycloak from './keycloak';
import { api } from '../services/axiosConfig';

const AuthProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const didInit = useRef(false);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/me');
      const data = response.data;
      setUserInfo(data);
      setIsWhitelisted(data.whitelisted);
      setIsAdmin(data.roles?.includes('ADMIN') || false);
      return data;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      setUserInfo(null);
      setIsWhitelisted(false);
      setIsAdmin(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (didInit.current) {
      return;
    }
    didInit.current = true;

    const initKeycloak = async () => {
      try {
        const auth = await keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        });

        setAuthenticated(auth);

        if (auth) {
          await fetchUserInfo();
        }

        setInitialized(true);

        keycloak.onTokenExpired = () => {
          keycloak.updateToken(30).catch(() => {
            console.error('Failed to refresh token');
            keycloak.logout();
          });
        };
      } catch (error) {
        console.error('Keycloak initialization failed:', error);
        setInitialized(true);
        setAuthenticated(false);
      }
    };

    initKeycloak();
  }, [fetchUserInfo]);

  const login = useCallback(() => {
    keycloak.login();
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const register = useCallback(() => {
    keycloak.register();
  }, []);

  const value = {
    keycloak,
    initialized,
    authenticated,
    userInfo,
    isWhitelisted,
    isAdmin,
    login,
    logout,
    register,
  };

  if (!initialized) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
